import { Button, IconButton, Typography } from '@sweep-io/sweep-design';
import { Star } from '@sweep-io/sweep-design/dist/icons';
import { Box, Tooltip } from '@mui/material';
import { useDocumentation } from '../useDocumentation';
import { useCallback, useState } from 'react';
import { telemetry } from '../../../telemetry';
import StarOff from './StarOff.svg?react'; //TODO add to design system
import { selectParsedObjects } from '../../../reducers/global/globalReducer';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../../reducers/userInfoReducer';
import { ObjectType } from '../../parser/ParserTypes';
import { CenteredCircularProgress } from '../../common/CenteredCircularProgress';

export enum AddObjectButtonVersion {
  small = 'small', //only icon button
  default = 'default', //with scanning object
  smallWithLabel = 'smallWithLabel',
  defaultWithLabel = 'defaultWithLabel',
}

interface AddObjectButtonProps {
  objectName: string;
  isLoading?: boolean;
  buttonVersion?: AddObjectButtonVersion;
  isInFunnelMap?: boolean;
}

export const AddObjectButton = ({
  objectName,
  isLoading,
  buttonVersion = AddObjectButtonVersion.default,
  isInFunnelMap,
}: AddObjectButtonProps) => {
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const parsedObjects = useSelector(selectParsedObjects(crmOrgId ?? ''));
  const parsedObject = parsedObjects?.find((object: ObjectType) => object.name === objectName);
  const _isFavorite = parsedObject?.visible || isInFunnelMap;

  const { onAddObject, onRemoveObject } = useDocumentation();
  const [isRemoving, setIsRemoving] = useState(false);
  const isWithLabel =
    buttonVersion === AddObjectButtonVersion.smallWithLabel ||
    buttonVersion === AddObjectButtonVersion.defaultWithLabel;

  const isDefaultVersion = buttonVersion === AddObjectButtonVersion.default;

  const onClick = useCallback(async () => {
    if (_isFavorite) {
      try {
        setIsRemoving(true);
        await onRemoveObject(objectName);
        setIsRemoving(false);
      } catch (e) {
        telemetry.captureError(e);
      }
    } else {
      onAddObject(objectName);
    }
  }, [_isFavorite, onAddObject, onRemoveObject, objectName]);

  if (isLoading && isDefaultVersion) {
    return (
      <Box display="flex" gap={1} pl={1} alignItems="center" minHeight="32px">
        <Typography variant="caption" whiteSpace="nowrap">
          Scanning object
        </Typography>

        <CenteredCircularProgress size={10} />
      </Box>
    );
  }

  return (
    <Tooltip
      disableHoverListener={isWithLabel}
      placement="top"
      title={
        isLoading
          ? 'Scanning object...'
          : isRemoving
            ? 'Removing object...'
            : _isFavorite
              ? 'Remove from favorites'
              : 'Add to favorites'
      }
    >
      <Box>
        {isWithLabel ? (
          <Button
            variant="system"
            size={buttonVersion === AddObjectButtonVersion.smallWithLabel ? 'tiny' : 'small'}
            startIconName={_isFavorite ? undefined : 'Star'}
            loading={isLoading || isRemoving}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            {_isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          </Button>
        ) : (
          <IconButton
            variant={isDefaultVersion ? 'flat' : 'system'}
            size="tiny"
            loading={isLoading || isRemoving}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            {_isFavorite ? <StarOff /> : <Star />}
          </IconButton>
        )}
      </Box>
    </Tooltip>
  );
};
